import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Card,
  CardContent,
  Grid,
  Chip,
  Divider,
  FormControlLabel,
  Switch,
  Tabs,
  Tab,
  Alert,
  RadioGroup,
  Radio,
  FormLabel
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Upload as UploadIcon,
  FileUpload as FileUploadIcon,
  DragIndicator as DragIcon,
  KeyboardArrowUp as UpIcon,
  KeyboardArrowDown as DownIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import { downloadAnnualFeedbackTemplate, parseAnnualFeedbackExcel } from '../../utils/annualFeedbackTemplate';

const SurveyCreatorComponent = ({ surveyData, onSave, mode, surveyTitle }) => {
  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState({
    title: '',
    type: 'text',
    isRequired: false,
    choices: [],
    section: '',
    ratingScale: 'likert' // New field for rating scale
  });
  const [newChoice, setNewChoice] = useState('');
  const [module, setModule] = useState('feedback');
  const [activeTab, setActiveTab] = useState(0);
  const [excelFile, setExcelFile] = useState(null);
  const [importError, setImportError] = useState('');
  const [importSuccess, setImportSuccess] = useState('');

  // Rating scale options
  const ratingScales = {
    likert: {
      name: 'Likert Scale',
      choices: [
        { value: 1, text: 'Strongly Disagree' },
        { value: 2, text: 'Disagree' },
        { value: 3, text: 'Neutral' },
        { value: 4, text: 'Agree' },
        { value: 5, text: 'Strongly Agree' }
      ]
    },
    satisfaction: {
      name: 'Satisfaction Scale',
      choices: [
        { value: 1, text: 'Very Dissatisfied' },
        { value: 2, text: 'Dissatisfied' },
        { value: 3, text: 'Neutral' },
        { value: 4, text: 'Satisfied' },
        { value: 5, text: 'Very Satisfied' }
      ]
    },
    frequency: {
      name: 'Frequency Scale',
      choices: [
        { value: 1, text: 'Never' },
        { value: 2, text: 'Rarely' },
        { value: 3, text: 'Sometimes' },
        { value: 4, text: 'Often' },
        { value: 5, text: 'Always' }
      ]
    },
    agreement: {
      name: 'Agreement Scale',
      choices: [
        { value: 1, text: 'Strongly Disagree' },
        { value: 2, text: 'Disagree' },
        { value: 3, text: 'Neutral' },
        { value: 4, text: 'Agree' },
        { value: 5, text: 'Strongly Agree' }
      ]
    }
  };

  useEffect(() => {
    if (mode === 'edit' && surveyData) {
      if (surveyData.questions) {
        setQuestions(surveyData.questions);
      }
      if (surveyData.module) {
        setModule(surveyData.module);
      }
    }
  }, [surveyData, mode]);

  const questionTypes = [
    { value: 'text', label: 'Text Input' },
    { value: 'comment', label: 'Long Text' },
    { value: 'radio', label: 'Single Choice' },
    { value: 'checkbox', label: 'Multiple Choice' },
    { value: 'rating', label: 'Rating' },
    { value: 'boolean', label: 'Yes/No' }
  ];

  // Generate automatic question ID
  const generateQuestionId = () => {
    return `question${questions.length + 1}`;
  };

  const addQuestion = () => {
    if (!currentQuestion.title) {
      toast.error('Please fill in question title');
      return;
    }

    const questionToAdd = {
      name: generateQuestionId(),
      ...currentQuestion,
      choices: currentQuestion.type === 'radio' || currentQuestion.type === 'checkbox' || currentQuestion.type === 'rating'
        ? currentQuestion.choices 
        : []
    };

    setQuestions([...questions, questionToAdd]);
    setCurrentQuestion({
      title: '',
      type: 'text',
      isRequired: false,
      choices: [],
      section: '',
      ratingScale: 'likert'
    });
    setNewChoice('');
  };

  const removeQuestion = (index) => {
    setQuestions(questions.filter((_, i) => i !== index));
  };

  const moveQuestion = (index, direction) => {
    if (direction === 'up' && index > 0) {
      const newQuestions = [...questions];
      [newQuestions[index], newQuestions[index - 1]] = [newQuestions[index - 1], newQuestions[index]];
      setQuestions(newQuestions);
    } else if (direction === 'down' && index < questions.length - 1) {
      const newQuestions = [...questions];
      [newQuestions[index], newQuestions[index + 1]] = [newQuestions[index + 1], newQuestions[index]];
      setQuestions(newQuestions);
    }
  };

  const addChoice = () => {
    if (newChoice.trim()) {
      setCurrentQuestion({
        ...currentQuestion,
        choices: [...currentQuestion.choices, { value: newChoice.trim(), text: newChoice.trim() }]
      });
      setNewChoice('');
    }
  };

  const removeChoice = (index) => {
    setCurrentQuestion({
      ...currentQuestion,
      choices: currentQuestion.choices.filter((_, i) => i !== index)
    });
  };

  // Handle rating scale change
  const handleRatingScaleChange = (scale) => {
    setCurrentQuestion({
      ...currentQuestion,
      ratingScale: scale,
      choices: ratingScales[scale].choices
    });
  };

  const handleExcelFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setExcelFile(file);
      setImportError('');
      setImportSuccess('');
    }
  };

  const handleExcelImport = () => {
    if (!excelFile) {
      setImportError('Please select an Excel file first');
      return;
    }

    parseAnnualFeedbackExcel(excelFile)
      .then((importedQuestions) => {
        if (importedQuestions.length === 0) {
          setImportError('No valid questions found in Excel file');
          return;
        }

        setQuestions([...questions, ...importedQuestions]);
        setImportSuccess(`Successfully imported ${importedQuestions.length} questions`);
        setExcelFile(null);
        
        // Reset file input
        const fileInput = document.getElementById('excel-file-input');
        if (fileInput) fileInput.value = '';
      })
      .catch((error) => {
        setImportError(error);
      });
  };

  const downloadTemplate = () => {
    downloadAnnualFeedbackTemplate();
  };

  const handleSave = () => {
    if (!surveyTitle || surveyTitle.trim() === '') {
      toast.error('Please enter a survey title');
      return;
    }

    if (questions.length === 0) {
      toast.error('Please add at least one question');
      return;
    }

    const surveyJson = {
      module,
      pages: [{
        name: 'page1',
        elements: questions
      }]
    };

    onSave(surveyJson);
  };

  const handleClearQuestions = () => {
    toast.warning('All questions have been cleared!', {
      autoClose: 3000,
      position: "top-right"
    });
    setQuestions([]);
  };

  const needsChoices = ['radio', 'checkbox'].includes(currentQuestion.type);
  const isRatingQuestion = currentQuestion.type === 'rating';

  // Group questions by section
  const groupedQuestions = questions.reduce((acc, question) => {
    const section = question.section || 'General';
    if (!acc[section]) {
      acc[section] = [];
    }
    acc[section].push(question);
    return acc;
  }, {});

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Survey Builder
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Create surveys by adding questions manually or importing from Excel
      </Typography>

      {/* Module Selection */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Survey Module
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Module</InputLabel>
            <Select
              value={module}
              onChange={(e) => setModule(e.target.value)}
              label="Module"
            >
              <MenuItem value="feedback">Feedback</MenuItem>
              <MenuItem value="brand">Brand</MenuItem>
            </Select>
          </FormControl>
        </CardContent>
      </Card>

      {/* Tabs for Manual vs Excel Import */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ mb: 3 }}>
            <Tab label="Manual Entry" />
            <Tab label="Excel Import" />
          </Tabs>

          {activeTab === 0 && (
            /* Manual Question Entry */
            <Box>
              <Typography variant="h6" gutterBottom>
                Add New Question
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={3}>
                Build your survey by adding questions one by one. Choose from different question types and organize them into sections for better structure.
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Question Title"
                    value={currentQuestion.title}
                    onChange={(e) => setCurrentQuestion({ ...currentQuestion, title: e.target.value })}
                    placeholder="e.g., What is your name?"
                    multiline
                    rows={2}
                    helperText="Examples: 'What is your name?', 'How satisfied are you with your work environment?', 'Which training programs have you completed?'"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Question Type</InputLabel>
                    <Select
                      value={currentQuestion.type}
                      onChange={(e) => setCurrentQuestion({ ...currentQuestion, type: e.target.value })}
                      label="Question Type"
                    >
                      {questionTypes.map((type) => (
                        <MenuItem key={type.value} value={type.value}>
                          {type.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Section (optional)"
                    value={currentQuestion.section}
                    onChange={(e) => setCurrentQuestion({ ...currentQuestion, section: e.target.value })}
                    placeholder="e.g., Personal Information, Work Experience"
                    helperText="Group questions into sections"
                  />
                </Grid>
                
                {isRatingQuestion && (
                  <Grid item xs={12} md={6}>
                    <FormControl component="fieldset">
                      <FormLabel component="legend">Rating Scale</FormLabel>
                      <RadioGroup
                        value={currentQuestion.ratingScale}
                        onChange={(e) => handleRatingScaleChange(e.target.value)}
                      >
                        {Object.entries(ratingScales).map(([key, scale]) => (
                          <FormControlLabel
                            key={key}
                            value={key}
                            control={<Radio />}
                            label={`${scale.name}: ${scale.choices.map(c => c.text).join(' | ')}`}
                          />
                        ))}
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                )}
                
                {needsChoices && (
                  <Grid item xs={12}>
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        Choices
                      </Typography>
                      <Box display="flex" gap={1} mb={2}>
                        <TextField
                          value={newChoice}
                          onChange={(e) => setNewChoice(e.target.value)}
                          placeholder="Add a choice"
                          size="small"
                          onKeyPress={(e) => e.key === 'Enter' && addChoice()}
                        />
                        <Button
                          variant="outlined"
                          onClick={addChoice}
                          disabled={!newChoice.trim()}
                          size="small"
                        >
                          Add
                        </Button>
                      </Box>
                      
                      {currentQuestion.choices.length > 0 && (
                        <Box>
                          {currentQuestion.choices.map((choice, index) => (
                            <Chip
                              key={index}
                              label={choice.text}
                              onDelete={() => removeChoice(index)}
                              sx={{ mr: 1, mb: 1 }}
                            />
                          ))}
                        </Box>
                      )}
                    </Box>
                  </Grid>
                )}
                
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={currentQuestion.isRequired}
                        onChange={(e) => setCurrentQuestion({ ...currentQuestion, isRequired: e.target.checked })}
                      />
                    }
                    label="Required question"
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={addQuestion}
                    disabled={!currentQuestion.title}
                  >
                    Add Question
                  </Button>
                </Grid>
              </Grid>
            </Box>
          )}

          {activeTab === 1 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Import Questions from Excel
              </Typography>
              
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>Excel Format:</strong> Your Excel file should have the following columns:
                </Typography>
                <Typography variant="body2" component="div" sx={{ mt: 1 }}>
                  • <strong>Title/Question:</strong> The question text (required)<br/>
                  • <strong>Type:</strong> text, comment, radio, checkbox, rating, boolean (required)<br/>
                  • <strong>Required:</strong> Yes/No or True/False (required)<br/>
                  • <strong>Section:</strong> Section name for grouping (optional)<br/>
                  • <strong>Rating Scale:</strong> For rating questions: likert, satisfaction, frequency (optional)<br/>
                  • <strong>Choices:</strong> Comma-separated choices for radio/checkbox questions (optional)
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={downloadTemplate}
                  sx={{ mt: 2, mr: 1 }}
                >
                  Download Template
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={downloadAnnualFeedbackTemplate}
                  sx={{ mt: 2 }}
                >
                  Download Annual Feedback Template
                </Button>
              </Alert>

              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={8}>
                  <input
                    id="excel-file-input"
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleExcelFileChange}
                    style={{ display: 'none' }}
                  />
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<FileUploadIcon />}
                    onClick={() => document.getElementById('excel-file-input').click()}
                    fullWidth
                  >
                    {excelFile ? excelFile.name : 'Choose Excel File'}
                  </Button>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Button
                    variant="contained"
                    startIcon={<UploadIcon />}
                    onClick={handleExcelImport}
                    disabled={!excelFile}
                    fullWidth
                  >
                    Import Questions
                  </Button>
                </Grid>
              </Grid>

              {importError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {importError}
                </Alert>
              )}

              {importSuccess && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  {importSuccess}
                </Alert>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Questions List */}
      {questions.length > 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Survey Questions ({questions.length})
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              Review and manage your survey questions. You can reorder questions, edit their properties, or remove them as needed. Questions are automatically grouped by sections for better organization.
            </Typography>
            
            {Object.entries(groupedQuestions).map(([section, sectionQuestions]) => (
              <Box key={section} sx={{ mb: 3 }}>
                <Typography variant="h6" color="primary" gutterBottom>
                  {section} ({sectionQuestions.length} question{sectionQuestions.length !== 1 ? 's' : ''})
                </Typography>
                
                {sectionQuestions.map((question, index) => {
                  const globalIndex = questions.findIndex(q => q.name === question.name);
                  return (
                    <Box key={question.name} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <DragIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                          <Typography variant="subtitle1" fontWeight="bold">
                            Q{globalIndex + 1}: {question.title}
                          </Typography>
                        </Box>
                        <Box display="flex" gap={0.5}>
                          <IconButton
                            size="small"
                            onClick={() => moveQuestion(globalIndex, 'up')}
                            disabled={globalIndex === 0}
                          >
                            <UpIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => moveQuestion(globalIndex, 'down')}
                            disabled={globalIndex === questions.length - 1}
                          >
                            <DownIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => removeQuestion(globalIndex)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </Box>
                      
                      <Box display="flex" gap={1} mb={1}>
                        <Chip label={question.type} size="small" color="primary" />
                        <Chip label={question.name} size="small" variant="outlined" />
                        {question.isRequired && (
                          <Chip label="Required" size="small" color="error" />
                        )}
                        {question.ratingScale && (
                          <Chip label={`Scale: ${ratingScales[question.ratingScale]?.name || question.ratingScale}`} size="small" color="secondary" />
                        )}
                      </Box>
                      
                      {question.choices && question.choices.length > 0 && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Choices:
                          </Typography>
                          <Box display="flex" gap={0.5} flexWrap="wrap" mt={0.5}>
                            {question.choices.map((choice, choiceIndex) => (
                              <Chip
                                key={choiceIndex}
                                label={choice.text}
                                size="small"
                                variant="outlined"
                              />
                            ))}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            ))}
            
            <Divider sx={{ my: 2 }} />
            
            {/* Survey Summary */}
            <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="h6" gutterBottom>
                Survey Summary
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <Typography variant="body2" color="text.secondary">
                    Total Questions: <strong>{questions.length}</strong>
                  </Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="body2" color="text.secondary">
                    Sections: <strong>{Object.keys(groupedQuestions).length}</strong>
                  </Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="body2" color="text.secondary">
                    Required Questions: <strong>{questions.filter(q => q.isRequired).length}</strong>
                  </Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="body2" color="text.secondary">
                    Module: <strong>{module}</strong>
                  </Typography>
                </Grid>
              </Grid>
            </Box>
            
            {(!surveyTitle || surveyTitle.trim() === '') && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Please enter a survey title to enable saving
              </Alert>
            )}
            
            {questions.length === 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Please add at least one question to save the survey
              </Alert>
            )}
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleClearQuestions}
                  fullWidth
                >
                  Clear All Questions
                </Button>
              </Grid>
              <Grid item xs={12} md={6}>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                  disabled={!surveyTitle || surveyTitle.trim() === '' || questions.length === 0}
                  fullWidth
                >
                  Save Survey
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default SurveyCreatorComponent; 